import React from 'react';
import { Box, Typography } from '@mui/material';
import AttachFile from '@mui/icons-material/AttachFile';
import Download from '@mui/icons-material/Download';
import Audiotrack from '@mui/icons-material/Audiotrack';
import PictureAsPdfOutlined from '@mui/icons-material/PictureAsPdfOutlined';
import { useTranslate } from 'react-admin';
import { sanitizeHtml } from '../../../Courses/editors/BlockEditor/sanitizeHtml';
import { getCalloutVariant } from '../../../Courses/editors/BlockEditor/calloutVariants';
import { formatFileSize, getEmbedUrl } from '../../../Courses/editors/BlockEditor/blockUtils';
import { useMediaObjectUrl, mediaUuidFrom } from '../../../../hooks/useMediaObjectUrl';

/* Content block players (ported from Scilora `block-player/content/*`), MUI + --oa-* tokens. */

export const RichtextPlayer: React.FC<{ html: string }> = ({ html }) => {
    if (!html) return null;
    return <div className="oa-bp-richtext" dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }} />;
};

export const HeadingPlayer: React.FC<{ text: string; level?: number }> = ({ text, level = 2 }) => {
    const lvl = Math.min(Math.max(level, 1), 6);
    if (!text) return null;
    return React.createElement(`h${lvl}`, { className: `oa-bp-heading oa-bp-plain oa-bp-h${lvl}` }, text);
};

export const QuotePlayer: React.FC<{ text: string; attribution?: string }> = ({ text, attribution }) => {
    if (!text) return null;
    return (
        <blockquote className="oa-bp-quote">
            <p>{text}</p>
            {attribution && <footer>— {attribution}</footer>}
        </blockquote>
    );
};

export const CodePlayer: React.FC<{ code: string; language?: string }> = ({ code, language }) => {
    if (!code) return null;
    return (
        <div className="oa-bp-code">
            {language && <div className="oa-bp-code-lang">{language}</div>}
            <pre><code>{code}</code></pre>
        </div>
    );
};

export const DividerPlayer: React.FC = () => <hr className="oa-bp-divider" />;

export const CalloutPlayer: React.FC<{ html: string; variant?: string }> = ({ html, variant }) => {
    const style = getCalloutVariant(variant);
    const Icon = style.icon;
    return (
        <Box className="oa-bp-callout" sx={{ backgroundColor: style.background, border: `1px solid ${style.border}`, color: style.color }} role="note">
            <Icon sx={{ color: style.iconColor, mt: 0.25 }} />
            <div className="oa-bp-richtext" dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }} />
        </Box>
    );
};

export const ImagePlayer: React.FC<{ src: string; alt?: string; caption?: string; alignment?: string }> = ({ src, alt, caption, alignment }) => {
    const url = useMediaObjectUrl(src);
    if (!src) return null;
    const justify = alignment === 'left' ? 'flex-start' : alignment === 'right' ? 'flex-end' : 'center';
    return (
        <figure className="oa-bp-figure" style={{ alignItems: justify }}>
            {url ? <img src={url} alt={alt ?? ''} className="oa-bp-image" loading="lazy" /> : <div className="oa-bp-media-skeleton" />}
            {caption && <figcaption>{caption}</figcaption>}
        </figure>
    );
};

export const VideoPlayer: React.FC<{ src: string; poster?: string; caption?: string }> = ({ src, poster, caption }) => {
    const url = useMediaObjectUrl(src);
    const posterUrl = useMediaObjectUrl(poster);
    if (!src) return null;
    return (
        <figure className="oa-bp-figure">
            {url ? <video src={url} poster={posterUrl ?? undefined} controls className="oa-bp-video" /> : <div className="oa-bp-media-skeleton" />}
            {caption && <figcaption>{caption}</figcaption>}
        </figure>
    );
};

export const AudioPlayer: React.FC<{ src: string; title?: string }> = ({ src, title }) => {
    const url = useMediaObjectUrl(src);
    if (!src) return null;
    return (
        <div className="oa-bp-card oa-bp-row">
            <span className="oa-bp-icon"><Audiotrack fontSize="small" /></span>
            <div className="oa-bp-grow">
                {title && <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>{title}</Typography>}
                {url ? <audio src={url} controls className="oa-bp-audio" /> : <div className="oa-bp-media-skeleton" style={{ height: 40 }} />}
            </div>
        </div>
    );
};

export const FilePlayer: React.FC<{ src: string; filename?: string; filesize?: number }> = ({ src, filename, filesize }) => {
    const translate = useTranslate();
    if (!src) return null;
    const uuid = mediaUuidFrom(src);
    const href = uuid ? `${process.env.REACT_APP_API_URL || ''}/api/media/download/${uuid}` : src;
    return (
        <div className="oa-bp-card oa-bp-row">
            <span className="oa-bp-icon"><AttachFile fontSize="small" /></span>
            <div className="oa-bp-grow">
                <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{filename || translate('blockPlayer.file.download')}</Typography>
                {!!filesize && <Typography variant="caption" color="text.secondary">{formatFileSize(filesize)}</Typography>}
            </div>
            <a className="oa-bp-iconbtn" href={href} download={filename} target="_blank" rel="noopener noreferrer" aria-label={translate('blockPlayer.file.download')}>
                <Download fontSize="small" />
            </a>
        </div>
    );
};

export const EmbedPlayer: React.FC<{ url: string; caption?: string; aspectRatio?: string }> = ({ url, caption, aspectRatio = '16:9' }) => {
    if (!url) return null;
    const embedUrl = getEmbedUrl(url);
    const padding = aspectRatio === '4:3' ? '75%' : aspectRatio === '1:1' ? '100%' : '56.25%';
    if (!embedUrl) {
        return <a className="oa-bp-card oa-bp-link" href={url} target="_blank" rel="noopener noreferrer">{caption || url}</a>;
    }
    return (
        <figure className="oa-bp-figure">
            <div className="oa-bp-embed-wrap" style={{ paddingBottom: padding }}>
                <iframe src={embedUrl} title={caption || url} allowFullScreen sandbox="allow-scripts allow-same-origin allow-popups allow-presentation" />
            </div>
            {caption && <figcaption>{caption}</figcaption>}
        </figure>
    );
};

export const PdfViewerPlayer: React.FC<{ src: string; filename?: string }> = ({ src, filename }) => {
    const translate = useTranslate();
    const url = useMediaObjectUrl(src);
    if (!src) return null;
    return (
        <div className="oa-bp-card oa-bp-flush">
            <div className="oa-bp-card-head"><PictureAsPdfOutlined fontSize="small" /><span>{filename || translate('blockPlayer.pdf.title')}</span></div>
            {url ? <iframe src={`${url}#toolbar=1&navpanes=0`} title={filename || 'PDF'} className="oa-bp-pdf" /> : <div className="oa-bp-media-skeleton" style={{ height: 240 }} />}
        </div>
    );
};

export const TablePlayer: React.FC<{ rows: string[][]; hasHeader?: boolean; caption?: string }> = ({ rows, hasHeader = true, caption }) => {
    if (!rows || rows.length === 0) return null;
    const header = hasHeader ? rows[0] : null;
    const body = hasHeader ? rows.slice(1) : rows;
    return (
        <figure className="oa-bp-figure">
            <div className="oa-bp-table-wrap">
                <table className="oa-bp-table">
                    {header && <thead><tr>{header.map((c, i) => <th key={i}>{c}</th>)}</tr></thead>}
                    <tbody>{body.map((row, r) => <tr key={r}>{row.map((c, i) => <td key={i}>{c}</td>)}</tr>)}</tbody>
                </table>
            </div>
            {caption && <figcaption>{caption}</figcaption>}
        </figure>
    );
};

export const TimelinePlayer: React.FC<{ entries: { date?: string; title?: string; description?: string }[] }> = ({ entries }) => {
    if (!entries || entries.length === 0) return null;
    return (
        <ol className="oa-bp-timeline">
            {entries.map((e, i) => (
                <li key={i}>
                    <span className="oa-bp-timeline-dot" />
                    <div>
                        {e.date && <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{e.date}</Typography>}
                        {e.title && <Typography variant="subtitle2">{e.title}</Typography>}
                        {e.description && <Typography variant="body2" color="text.secondary">{e.description}</Typography>}
                    </div>
                </li>
            ))}
        </ol>
    );
};
